/**
 * `useOsFileDrop` — the shared, headless core of "drag a file off your
 * desktop onto a surface to upload it".
 *
 * Generalized from `src/features/chat/components/ChatDropZone.tsx` so the
 * card description + comment composer surfaces reuse the exact same
 * native-HTML5 mechanics (chat keeps its own component). The full pattern
 * + pitfalls live in `docs/os-file-drag-and-drop.md`.
 *
 * Native `dragenter/dragover/dragleave/drop` — NOT dnd-kit. Coexists with
 * any in-app pointer drag on the same surface because every handler
 * early-returns unless the drag carries files (`dataTransfer.types`
 * includes `'Files'`). `dragCounter` (increment on enter, floored
 * decrement on leave, gated on the exact 0↔1 transitions) prevents the
 * flicker a naive boolean strobes when the cursor crosses child elements.
 *
 * Headless by design: the hook owns the drag STATE and returns handlers
 * to spread onto a wrapper element; the caller renders whatever overlay
 * it wants (see `<FileDropOverlay>`) and supplies its own copy. Directory
 * rejection is detected here (a dropped folder can't be told apart from a
 * file by `dataTransfer.files` alone) but the toast copy is the caller's
 * via `onDirectoryRejected`. MIME / size / count validation lives
 * downstream in the caller's accept path — browsers hide File MIME types
 * during a drag, so we accept the drag visually and reject afterwards.
 */

import { type DragEvent, useCallback, useRef, useState } from 'react'

export interface UseOsFileDropOptions {
  /** When false, the handlers pass drag events through untouched. */
  enabled?: boolean
  /**
   * Called with every non-directory file dropped. Filtering is upstream.
   * `point` is the drop's viewport coordinates, for callers that place
   * the files where they were let go.
   */
  onFilesDropped: (files: File[], point: { x: number; y: number }) => void
  /** Called once per drop that contained at least one directory entry. */
  onDirectoryRejected?: () => void
}

export interface OsFileDropHandlers {
  onDragEnter: (event: DragEvent<HTMLElement>) => void
  onDragOver: (event: DragEvent<HTMLElement>) => void
  onDragLeave: (event: DragEvent<HTMLElement>) => void
  onDrop: (event: DragEvent<HTMLElement>) => void
}

/** True when a drag carries OS files (vs an in-app pointer drag). */
function dragHasFiles(event: DragEvent<HTMLElement>): boolean {
  const types = event.dataTransfer?.types
  if (!types) return false
  return Array.from(types as ArrayLike<string>).includes('Files')
}

export function useOsFileDrop({
  enabled = true,
  onFilesDropped,
  onDirectoryRejected,
}: UseOsFileDropOptions): { isDragActive: boolean; dropHandlers: OsFileDropHandlers } {
  const [isDragActive, setIsDragActive] = useState(false)
  const dragCounter = useRef(0)

  const onDragEnter = useCallback(
    (event: DragEvent<HTMLElement>) => {
      if (!enabled || !dragHasFiles(event)) return
      event.preventDefault()
      dragCounter.current += 1
      if (dragCounter.current === 1) setIsDragActive(true)
    },
    [enabled],
  )

  const onDragOver = useCallback(
    (event: DragEvent<HTMLElement>) => {
      if (!enabled || !dragHasFiles(event)) return
      // Required: without preventDefault on dragover the drop event never
      // fires — the browser default is to reject the drop.
      event.preventDefault()
      event.dataTransfer.dropEffect = 'copy'
    },
    [enabled],
  )

  const onDragLeave = useCallback(
    (event: DragEvent<HTMLElement>) => {
      if (!enabled || !dragHasFiles(event)) return
      event.preventDefault()
      dragCounter.current = Math.max(0, dragCounter.current - 1)
      if (dragCounter.current === 0) setIsDragActive(false)
    },
    [enabled],
  )

  const onDrop = useCallback(
    (event: DragEvent<HTMLElement>) => {
      if (!enabled) return
      event.preventDefault()
      dragCounter.current = 0
      setIsDragActive(false)
      const dt = event.dataTransfer

      // Prefer the items API so directory entries can be detected +
      // rejected (a dropped folder surfaces as an item whose entry
      // `isDirectory` is true; `dataTransfer.files` alone can't tell them
      // apart reliably across browsers).
      const items = Array.from(dt?.items ?? [])
      const files: File[] = []
      let hadDirectory = false
      if (items.length > 0) {
        for (const item of items) {
          if (item.kind !== 'file') continue
          const entry = item.webkitGetAsEntry?.()
          if (entry?.isDirectory) {
            hadDirectory = true
            continue
          }
          const file = item.getAsFile()
          if (file) files.push(file)
        }
      } else {
        files.push(...Array.from(dt?.files ?? []))
      }

      if (hadDirectory) onDirectoryRejected?.()
      if (files.length === 0) return
      onFilesDropped(files, { x: event.clientX, y: event.clientY })
    },
    [enabled, onFilesDropped, onDirectoryRejected],
  )

  return {
    isDragActive,
    dropHandlers: { onDragEnter, onDragOver, onDragLeave, onDrop },
  }
}
